/**
 * 가입 · 소셜 · 초대 수락의 포트. (docs/decisions/0225)
 *
 * `session-ports.ts` 의 넷(로그인·재설정)과 **같은 자리·같은 이유**다: 이 문들은 부트 전에
 * 불리고, 라우터 타입은 vertical 을 합치므로 코어가 이름을 부를 수 없다. 그래서 코어는 부르는
 * 함수의 모양만 알고 진짜 함수는 composition root 가 넘긴다.
 *
 * 문이 아홉이라 넷처럼 하나씩 `…PortFrom` 을 두지 않고 **한 묶음**으로 접는다. 접는 규칙은 넷과
 * 같다 — 화면이 사용자에게 할 말이 갈래마다 다르다: 고쳐서 다시(거절) · 잠시 뒤(한도) ·
 * 서버가 이상하다(그 밖). 하나로 접으면 서버가 죽은 날 사람이 자기 링크를 의심한다.
 */
import type {
  AuthClient, InviteInspectDto, LoginResultDto, OnboardingSignInDto, SignupInspectDto, SocialCallbackDto,
  SocialProvider, SocialStartDto,
} from '@worhythm/contracts';
import { classifyRpcError, type RpcFailure } from './errors.js';

export type PortOutcome<T> =
  | { readonly kind: 'ok'; readonly value: T }
  | { readonly kind: 'rejected'; readonly messageKey: string; readonly nextActionKey?: string }
  | { readonly kind: 'rateLimited'; readonly retryAfterMs?: number }
  | {
      readonly kind: 'unavailable';
      readonly messageKey: string;
      readonly nextActionKey?: string;
      readonly detail?: string;
    };

/** 서버의 문 그대로의 모양. composition root 가 `rpc.client.session.*` 를 넘긴다. */
export interface OnboardingCalls {
  socialProviders(input: Record<string, never>): Promise<{ readonly providers: readonly SocialProvider[] }>;
  signupStart(input: { email: string }): Promise<unknown>;
  signupInspect(input: { via: 'email' | 'social'; token: string }): Promise<SignupInspectDto>;
  signupComplete(input: {
    via: 'email' | 'social'; token: string; name: string; companyName: string;
    password?: string; phone?: string; agreed: true;
  }): Promise<OnboardingSignInDto>;
  socialStart(input: {
    provider: SocialProvider; intent: 'login' | 'invite'; inviteToken?: string; client?: AuthClient;
  }): Promise<SocialStartDto>;
  socialCallback(input: { provider: SocialProvider; code: string; state: string }): Promise<SocialCallbackDto>;
  socialChoose(input: { ticket: string; tenantId: string }): Promise<OnboardingSignInDto>;
  /** 앱(0226) — 브라우저에서 끝난 로그인의 티켓을 세션으로. */
  appTicketRedeem(input: { ticket: string; appVerifier: string }): Promise<LoginResultDto>;
  inviteInspect(input: { token: string }): Promise<InviteInspectDto>;
  inviteAccept(input: { token: string; password: string }): Promise<LoginResultDto>;
}

type Ported<C> = {
  readonly [K in keyof C]: C[K] extends (input: infer I) => Promise<infer O>
    ? (input: I) => Promise<PortOutcome<O>>
    : never;
};

export type OnboardingPorts = Ported<OnboardingCalls>;

function retryAfterMsOf(failure: RpcFailure): number | undefined {
  const d = failure.details;
  if (typeof d !== 'object' || d === null) return undefined;
  const v = (d as Record<string, unknown>).retryAfterMs;
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined;
}

/** 실패 하나를 갈래로. `loginPortFrom` 과 같은 순서다 — 한도를 거절보다 먼저 본다. */
export function outcomeOfFailure(failure: RpcFailure): Exclude<PortOutcome<never>, { kind: 'ok' }> {
  if (failure.code === 'rateLimit.exceeded') {
    const retryAfterMs = retryAfterMsOf(failure);
    return retryAfterMs === undefined ? { kind: 'rateLimited' } : { kind: 'rateLimited', retryAfterMs };
  }
  if (failure.kind === 'app' || failure.kind === 'unauthenticated') {
    return {
      kind: 'rejected', messageKey: failure.messageKey,
      ...(failure.nextActionKey === undefined ? {} : { nextActionKey: failure.nextActionKey }),
    };
  }
  return {
    kind: 'unavailable', messageKey: failure.messageKey,
    ...(failure.nextActionKey === undefined ? {} : { nextActionKey: failure.nextActionKey }),
    detail: `${failure.code ?? 'unclassified'}${failure.status ? ` · HTTP ${failure.status}` : ''}`,
  };
}

export function onboardingPortsFrom(
  calls: OnboardingCalls,
  classify: (err: unknown) => RpcFailure = classifyRpcError,
): OnboardingPorts {
  const wrap = <I, O>(call: (input: I) => Promise<O>) => async (input: I): Promise<PortOutcome<O>> => {
    try {
      return { kind: 'ok', value: await call(input) };
    } catch (e) {
      return outcomeOfFailure(classify(e));
    }
  };
  return {
    socialProviders: wrap((i: Record<string, never>) => calls.socialProviders(i)),
    signupStart: wrap((i: { email: string }) => calls.signupStart(i)),
    signupInspect: wrap((i: Parameters<OnboardingCalls['signupInspect']>[0]) => calls.signupInspect(i)),
    signupComplete: wrap((i: Parameters<OnboardingCalls['signupComplete']>[0]) => calls.signupComplete(i)),
    socialStart: wrap((i: Parameters<OnboardingCalls['socialStart']>[0]) => calls.socialStart(i)),
    socialCallback: wrap((i: Parameters<OnboardingCalls['socialCallback']>[0]) => calls.socialCallback(i)),
    socialChoose: wrap((i: Parameters<OnboardingCalls['socialChoose']>[0]) => calls.socialChoose(i)),
    inviteInspect: wrap((i: { token: string }) => calls.inviteInspect(i)),
    inviteAccept: wrap((i: { token: string; password: string }) => calls.inviteAccept(i)),
    appTicketRedeem: wrap((i: { ticket: string; appVerifier: string }) => calls.appTicketRedeem(i)),
  };
}
